import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Pencil, Trash2, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { titleize } from "@/lib/format";

const EMPTY = { name: "", start_time: "08:00", end_time: "14:00", department: "",
  effective_from: "", active: true, staff_ids: [] };

export default function Shifts() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const canManage = can("staff.shifts.manage");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/shifts");
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => {
    if (!can("staff.shifts.view")) return;
    load();
    if (can("staff.shifts.manage")) {
      api.get("/shifts/assignable-staff").then(({ data }) => setStaff(data.items)).catch(() => {});
    }
  }, [load, can]);

  if (!can("staff.shifts.view")) return <NoAccess what="staff shift management" />;

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name || "", start_time: s.start_time || "08:00", end_time: s.end_time || "14:00",
      department: s.department || "", effective_from: (s.effective_from || "").slice(0, 10),
      active: s.active !== false, staff_ids: s.staff_ids || [] });
    setFormError(""); setOpen(true);
  };

  const toggleStaff = (id) => setForm((f) => ({ ...f,
    staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id] }));

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    if (!form.name.trim()) { setFormError("Shift name is required."); return; }
    try {
      if (editing) await api.patch(`/shifts/${editing.shift_id}`, form);
      else await api.post("/shifts", form);
      toast.success(editing ? "Shift updated." : "Shift created.");
      setOpen(false); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"? Historical attributions are preserved.`)) return;
    try { await api.delete(`/shifts/${s.shift_id}`); toast.success("Shift deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} />;
  if (!items) return <Loading />;

  return (
    <div data-testid="shifts-page">
      <PageHeader overline="Administration" title="Staff shifts"
        subtitle="Define working shifts (e.g. Morning 08:00–14:00) and assign multiple staff to each. The shift at the time of a transaction is preserved for attribution and reporting."
        actions={canManage && <button onClick={openCreate} className={primaryBtnCls} data-testid="add-shift-btn"><Clock className="h-4 w-4" strokeWidth={1.8} /> Create shift</button>} />

      <Table testid="shifts-table" columns={["Shift", "Timing", "Department", "Staff assigned", "Status", "Actions"]}
        rows={items} empty={<EmptyState title="No shifts yet" subtitle="Create Morning, Evening and Night shifts, then assign your staff." testid="shifts-empty" />}
        renderRow={(s) => (
          <tr key={s.shift_id} data-testid={`shift-row-${s.shift_id}`}>
            <Td className="font-medium text-slate-900">{s.name}</Td>
            <Td className="text-slate-600 whitespace-nowrap">{s.start_time} – {s.end_time}</Td>
            <Td className="text-slate-600">{s.department || "—"}</Td>
            <Td className="text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-slate-400" />
                {(s.staff || []).length === 0 ? "None"
                  : (s.staff || []).map((x) => `${x.name}`).join(", ")}</span>
            </Td>
            <Td><StatusPill status={s.active === false ? "inactive" : "active"} /></Td>
            <Td>
              {canManage && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(s)} title="Edit" data-testid={`edit-shift-${s.shift_id}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#EA580C] hover:text-[#EA580C]">
                    <Pencil className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                  <button onClick={() => remove(s)} title="Delete" data-testid={`delete-shift-${s.shift_id}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-red-500 hover:text-red-600">
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              )}
            </Td>
          </tr>
        )} />

      <Modal open={open} onClose={() => setOpen(false)} wide title={editing ? "Edit shift" : "Create shift"}
        subtitle="Assign as many staff members as work this shift." testid="shift-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
          <Field label="Shift name *"><input className={inputCls} data-testid="shift-name-input" value={form.name}
            placeholder="Morning Shift" onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Department (optional)"><input className={inputCls} data-testid="shift-department-input"
            value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
          <Field label="Start time *"><input type="time" className={inputCls} data-testid="shift-start-input"
            value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></Field>
          <Field label="End time *"><input type="time" className={inputCls} data-testid="shift-end-input"
            value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></Field>
          <Field label="Effective from (optional)"><input type="date" className={inputCls}
            value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></Field>
          <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm self-end">
            <input type="checkbox" checked={form.active} data-testid="shift-active-checkbox"
              onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#0F172A]" />
            Active
          </label>
          <div className="sm:col-span-2">
            <p className="overline text-[#EA580C] mb-3">Assign staff ({form.staff_ids.length})</p>
            <div className="border border-slate-200 rounded-lg divide-y max-h-72 overflow-auto" data-testid="shift-staff-list">
              {staff.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">No active staff found.</p>}
              {staff.map((u) => (
                <label key={u.user_id} className="flex items-center justify-between px-3.5 py-2.5 text-sm cursor-pointer hover:bg-slate-50"
                  data-testid={`shift-staff-option-${u.user_id}`}>
                  <span className="text-slate-700">{u.name} <span className="text-slate-400">· {titleize(u.role)}{u.department ? ` · ${u.department}` : ""}</span></span>
                  <input type="checkbox" checked={form.staff_ids.includes(u.user_id)}
                    onChange={() => toggleStaff(u.user_id)} className="h-4 w-4 accent-[#0F172A]" />
                </label>
              ))}
            </div>
          </div>
          {formError && <p className="sm:col-span-2 text-sm font-medium text-red-600" data-testid="shift-form-error">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="shift-submit-btn">{editing ? "Save changes" : "Create shift"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
